import type {
  ActivityEvent,
  Lead,
  Membership,
  Stage,
  Task,
} from "../../lib/types";
import { civilDate, daysBetween } from "../notifications/feed";

/**
 * Modelo puro do perfil do lead.
 *
 * O perfil é a única superfície que mostra a jornada inteira, então três coisas
 * precisam ser exatas aqui: o que a linha do tempo diz de cada registro, o que
 * pode ser desfeito e o que a permanência em cada etapa realmente significa.
 *
 * Nada aqui inventa duração. O tempo na etapa atual vem da entrada registrada
 * na participação; o das etapas passadas vem do rótulo que o próprio evento de
 * transição gravou. Quando não há registro, a resposta é "—", não uma média
 * (AGENTS §7.3, §10).
 */

export type ProfileTabId = "historico" | "notas" | "tarefas" | "contratos";

export interface ProfileTab {
  id: ProfileTabId;
  label: string;
}

export const PROFILE_TABS: ProfileTab[] = [
  { id: "historico", label: "Histórico" },
  { id: "notas", label: "Notas" },
  { id: "tarefas", label: "Tarefas" },
  { id: "contratos", label: "Contratos e recebimentos" },
];

export function isProfileTab(value: string | null): value is ProfileTabId {
  return PROFILE_TABS.some((tab) => tab.id === value);
}

/* --- Jornada no funil -------------------------------------------------------- */

export interface StageStep {
  stage: Stage;
  state: "past" | "current" | "future";
  /** Permanência registrada; ausente quando o domínio não a gravou. */
  durationLabel?: string;
}

export interface StageJourneyInput {
  stages: Stage[];
  membership?: Membership;
  events: ActivityEvent[];
  now: string;
}

/**
 * Etapas do funil na ordem, com a posição atual da participação.
 *
 * A permanência de uma etapa passada vem do último evento de transição que
 * apontou para ela; a da etapa atual é contada a partir da entrada registrada.
 */
export function stageJourney(input: StageJourneyInput): StageStep[] {
  const { stages, membership, events, now } = input;
  const ordered = [...stages].sort((a, b) => a.order - b.order);
  const currentIndex = membership
    ? ordered.findIndex((stage) => stage.id === membership.stageId)
    : -1;

  return ordered.map((stage, index) => {
    const state: StageStep["state"] =
      currentIndex < 0 ? "future" : index < currentIndex ? "past" : index === currentIndex ? "current" : "future";

    if (state === "current" && membership) {
      const days = daysBetween(civilDate(membership.enteredStageAt), civilDate(now));
      return {
        stage,
        state,
        durationLabel: days === 0 ? "hoje" : `${days} ${days === 1 ? "dia" : "dias"}`,
      };
    }

    if (state === "past") {
      // O filtro por participação é obrigatório: sem ele, a permanência de um
      // lead vazaria para a jornada de outro que passou pela mesma etapa.
      const recorded = events
        .filter(
          (event) =>
            event.kind === "stage" &&
            event.stageId === stage.id &&
            event.membershipId === membership?.id &&
            event.durationLabel,
        )
        .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
        .at(-1);
      return { stage, state, durationLabel: recorded?.durationLabel };
    }

    return { stage, state };
  });
}

/** Dias desde o cadastro, em dias civis de São Paulo. */
export function daysInFunnel(lead: Lead, now: string): number {
  return daysBetween(civilDate(lead.createdAt), civilDate(now));
}

/** Dias desde a última interação real; nulo quando nunca houve uma (§7.7). */
export function quietDays(lead: Lead, now: string): number | null {
  if (!lead.lastInteractionAt) return null;
  return daysBetween(civilDate(lead.lastInteractionAt), civilDate(now));
}

/* --- Linha do tempo ---------------------------------------------------------- */

export type TimelineTone =
  | "stage"
  | "note"
  | "task"
  | "message"
  | "file"
  | "outcome"
  | "finance"
  | "meeting"
  | "other";

export interface TimelineEntry {
  event: ActivityEvent;
  tone: TimelineTone;
  /** Rótulo curto do tipo, exibido junto da data. */
  label: string;
  /** Dia civil, para agrupar sem depender do fuso do navegador. */
  day: string;
  /** Este registro aceita desfazer ou remover. */
  removable: boolean;
}

const TONE_BY_TYPE: Array<[RegExp, TimelineTone, string]> = [
  [/^stage_changed$/, "stage", "Etapa"],
  [/^note_added$/, "note", "Nota"],
  [/^task_/, "task", "Tarefa"],
  [/^(message_sent|email_sent|communication_message|lead_replied|follow_up)$/, "message", "Mensagem"],
  [/^(file_attached|link_added)$/, "file", "Arquivo"],
  [/^meeting_/, "meeting", "Reunião"],
  [
    /^(contract_created|contract_cancelled|invoice_extra_added|receipt_registered|lead_converted)$/,
    "finance",
    "Financeiro",
  ],
  [/^ai_/, "message", "Sugestão"],
  [/^(lead_won|lead_lost|lead_reactivated)$/, "outcome", "Desfecho"],
  [/^(lead_created|membership_created)$/, "other", "Cadastro"],
];

export function eventPresentation(event: ActivityEvent): { tone: TimelineTone; label: string } {
  for (const [pattern, tone, label] of TONE_BY_TYPE)
    if (pattern.test(event.type)) return { tone, label };
  if (event.kind === "outcome") return { tone: "outcome", label: "Desfecho" };
  if (event.kind === "stage") return { tone: "stage", label: "Etapa" };
  return { tone: "other", label: "Registro" };
}

/**
 * Registros que explicam a existência do cadastro não podem ser removidos como
 * cartões soltos: sem eles, a jornada deixaria de explicar o estado (§7.4).
 */
export function isRemovable(event: ActivityEvent): boolean {
  if (event.kind === "stage") return event.type === "stage_changed";
  if (event.kind === "outcome")
    return ["lead_won", "lead_lost", "lead_reactivated"].includes(event.type);
  return !["lead_created", "membership_created"].includes(event.type);
}

/** Linha do tempo do lead, do mais recente para o mais antigo. */
export function timeline(events: ActivityEvent[], leadId: string): TimelineEntry[] {
  return events
    .filter((event) => event.leadId === leadId)
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
    .map((event) => ({
      event,
      ...eventPresentation(event),
      day: civilDate(event.occurredAt),
      removable: isRemovable(event),
    }));
}

/* --- Desfazer ---------------------------------------------------------------- */

export interface UndoPlan {
  /** Pergunta da confirmação, dizendo exatamente o que vai acontecer. */
  question: string;
  /** O comando também reverte o estado atual, além de apagar o registro. */
  reverts: boolean;
}

/**
 * O que desfazer este registro faz.
 *
 * Só o último movimento e o último desfecho revertem o estado; um registro do
 * meio da jornada apenas sai da lista, e a confirmação precisa dizer isso para
 * ninguém esperar uma reversão que não vem.
 */
export function undoPlan(
  event: ActivityEvent,
  events: ActivityEvent[],
  stages: Stage[],
): UndoPlan {
  const sameMembership = (kind: ActivityEvent["kind"], types: string[]) =>
    events
      .filter(
        (item) =>
          item.membershipId === event.membershipId &&
          item.kind === kind &&
          types.includes(item.type) &&
          item.id !== event.id,
      )
      .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

  if (event.kind === "stage") {
    const others = sameMembership("stage", ["stage_changed"]);
    const latest = !others.length || event.occurredAt >= others.at(-1)!.occurredAt;
    const previous = event.fromStageId
      ? stages.find((stage) => stage.id === event.fromStageId)
      : undefined;
    if (latest && previous)
      return {
        question: `Desfazer a movimentação para ${event.title}? A etapa atual volta para ${previous.name} e este registro sai da jornada.`,
        reverts: true,
      };
    return {
      question: "Remover esta passagem da jornada? A etapa atual não muda.",
      reverts: false,
    };
  }

  if (event.kind === "outcome") {
    const others = sameMembership("outcome", ["lead_won", "lead_lost", "lead_reactivated"]);
    const latest = !others.length || event.occurredAt >= others.at(-1)!.occurredAt;
    const previous = others.at(-1);
    const previousLabel =
      event.previousStatus === "won"
        ? "ganha"
        : event.previousStatus === "lost"
          ? "perdida"
          : previous?.type === "lead_won"
            ? "ganha"
            : previous?.type === "lead_lost"
              ? "perdida"
              : "ativa";
    if (latest)
      return {
        question: `Desfazer ${event.title.toLowerCase()}? A participação volta para o status ${previousLabel}.`,
        reverts: true,
      };
    return {
      question: "Remover este desfecho da jornada? O status atual não muda.",
      reverts: false,
    };
  }

  return { question: "Excluir este registro da jornada?", reverts: false };
}

/* --- Situação derivada -------------------------------------------------------- */

export interface Situation {
  tone: "good" | "warn" | "crit" | "accent";
  title: string;
  text: string;
}

export interface SituationInput {
  status?: Membership["status"];
  quietDays: number | null;
  pendingTasks: number;
  isClient: boolean;
}

/** Limite a partir do qual o silêncio vira aviso, em dias civis. */
export const QUIET_ALERT_DAYS = 7;

/**
 * Leitura do estado a partir dos registros: desfecho, silêncio e tarefas.
 *
 * É derivação determinística do que está gravado, não análise de IA, e o texto
 * da tela diz isso (AGENTS §3, §10).
 */
export function situation(input: SituationInput): Situation {
  const { status, quietDays: quiet, pendingTasks, isClient } = input;
  const stale = quiet !== null && quiet >= QUIET_ALERT_DAYS;

  if (status === "lost")
    return {
      tone: "crit",
      title: "Negociação perdida",
      text: "Consulte o motivo registrado antes de decidir por uma reabertura.",
    };

  if (status === "won")
    return {
      tone: "good",
      title: "Negociação ganha",
      text: "Combine o acompanhamento e registre os contratos desta conta.",
    };

  if (stale)
    return {
      tone: "warn",
      title: "Hora de retomar o contato",
      text: `Sem interação registrada há ${quiet} dias. Retome e confirme as prioridades atuais.`,
    };

  if (pendingTasks === 0)
    return {
      tone: "accent",
      title: "Sem próximo passo marcado",
      text: `Combine o próximo passo com ${isClient ? "o cliente" : "o lead"} e registre a tarefa.`,
    };

  return {
    tone: "accent",
    title: "Próximo passo em aberto",
    text: `${pendingTasks} ${pendingTasks === 1 ? "tarefa pendente" : "tarefas pendentes"} para este cadastro.`,
  };
}

/** Tarefas pendentes do lead, da mais próxima do vencimento para a mais distante. */
export function pendingTasksOf(tasks: Task[], leadId: string): Task[] {
  return tasks
    .filter((task) => task.leadId === leadId && task.status !== "done")
    .sort((a, b) => a.dueAt.localeCompare(b.dueAt));
}
